import { get, isEmpty } from 'lodash/fp';
import {
    BaseSyntheticEvent,
    ChangeEvent,
    isValidElement,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTheme } from 'styled-components';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ElementGetter, getComponentProps, InputValue, Valued } from '@optimus-web/common';
import { MaxSizeChangeProps, Positioner, useClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { AutosizeInput as AutosizeInputComponent } from '../../AutosizeInput';
import { BareIconButton } from '../../BareIconButton';
import { Chip } from '../../Chip';
import { Affix } from '../../common/Affix';
import { Container } from '../../Container';
import { Divider } from '../../Divider';
import { Drawer } from '../../Drawer';
import { LabeledDropdownItem } from '../../DropdownItem';
import { ExpandIcon } from '../../ExpandIcon';
import { Icon } from '../../Icon';
import { Image } from '../../Image';
import { Input } from '../../Input';
import { DropdownFooter, IOpenableOverlay, OpenableOverlay } from '../../OpenableOverlay';
import { Spinner } from '../../Spinner';
import { Stack, StackItem } from '../../Stack';
import { Disabled, RenderDropdownIconProps, Selected } from '../../types';
import { getBreakpointRangeValueFallback } from '../../utils/breakpointsFallback';
import { flattenChildren, isOptionGroup, useFilteredItems, useKeyboardNavigation, useSelectAll } from '../hooks';
import { findFirstSelectableIndex } from '../hooks/useKeyboardNavigation';
import { SelectBaseProps } from '../types';
import { defaultSearchValueMapper, makeListId, useValueMapper } from '../utils';
import { ensureString } from '../../utils';
import { SelectAllItem } from './SelectAllItem';
import { ISelectList, SelectList } from './SelectList';
import { DEFAULT_DESKTOP_ITEM_HEIGHT, DEFAULT_MOBILE_ITEM_HEIGHT } from './SelectList/SelectList';
import {
    ComboInputElement,
    DrawerInput,
    LeftDetailContainer,
    RestTagCounterContainer,
    RightDetailContainer,
    RightVariantContainer,
    SearchContainerElement,
    SingleValueElement,
    SpinnerContainer,
    ValueContainerElement,
} from './styles/style';

const DEFAULT_DROPDOWN_MIN_WIDTH = 240;

const Select = ({
    name,
    selectAllEnabled = true,
    selected,
    children,
    searchValueMapper = defaultSearchValueMapper,
    customSearch,
    shadowField,
    overlayMaxHeight = 500,
    dropdownMinWidth,
    renderDropdownIcon = ({ toggle, open, disabled, readOnly, multiselect, compact }: RenderDropdownIconProps) => (
        <ExpandIcon
            variant="basic"
            disabled={disabled || (readOnly && (!multiselect || !compact))}
            onClick={toggle}
            expanded={open}
            size="small"
        />
    ),
    multiselect = false,
    portalContainerId,
    placeholder,
    searchPlaceholder,
    autoCompleteAttribute,
    defaultItemHeight = DEFAULT_DESKTOP_ITEM_HEIGHT,
    mobileDefaultItemHeight = DEFAULT_MOBILE_ITEM_HEIGHT,
    disabled,
    readOnly,
    required,
    size: customSize,
    emptyMessage,
    sizeToAnchor = true,
    value,
    compact,
    inputType,
    label,
    virtualized,
    mobileOptionsHeader,
    helper,
    captionIconHandler,
    captionIconWithTooltip,
    inline,
    insideCompoundField,
    autoFocus,
    onChange,
    onBlur,
    onClear,
    onFocus,
    onSearchChange,
    onSelectAll,
    registerScrollAway,
    deregisterScrollAway,
    displayMapper = (c: React.ReactElement<LabeledDropdownItem>) => c.props.label,
    valueMapper: valueMapperFromProps,
    breakpointRange,
    iconName,
    leadingImage,
    suffix,
    trailingImage,
    loader,
    inputValuePattern,
    disableInputSearch = false,
    preserveMultiSelectSearch = false,
    dataTestId,
    clearable,
    footer,
    error,
    ...other
}: SelectBaseProps) => {
    // Utils and configs
    const { registerClickAway, deregisterClickAway } = useClickAway();
    const theme = useTheme();
    const { t } = useLocalization();
    const { size } = getComponentProps(theme, 'Select', { size: customSize });
    const valueMapper = useValueMapper(valueMapperFromProps);
    const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange));
    const selectAll = multiselect && selectAllEnabled;

    // State
    const [opened, setOpened] = useState(false);
    const [focused, setFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(() => findFirstSelectableIndex(children, selectAll));
    const [searchValue, setSearchValue] = useState('');
    const [keyboardHandled, setKeyboardHandled] = useState(false);
    const [indexChangeReason, setIndexChangeReason] = useState<'user' | 'internal'>();
    const [restTagCount, setRestTagCount] = useState(0);

    // Refs
    const comboInputRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const drawerRef = useRef<HTMLDivElement>(null);
    const overlayComponentRef = useRef<IOpenableOverlay | null>(null);
    const valueContainerRef = useRef<HTMLDivElement>(null);
    const tagsRef = useRef<Record<string, HTMLDivElement | null>>({});
    const drawerInputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const listComponentRef = useRef<HTMLDivElement & ISelectList>(null);

    // Handlers
    const handleClearClick = (e: BaseSyntheticEvent) => {
        e.stopPropagation();

        setSearchValue('');

        if (opened) {
            close();
        }

        onFocus?.();
        inputRef.current?.focus();
        onChange?.(e as ChangeEvent, multiselect ? [] : '');
        onClear?.();
    };

    const handleBlur = useCallback(() => {
        if (focused) {
            setFocused(false);
            onBlur?.();
        }
    }, [focused, onBlur]);

    const handleFocus = useCallback(() => {
        if (focused) return;

        setFocused(true);
        onFocus?.();
    }, [focused, onFocus]);

    const close = useCallback(() => {
        if (opened) {
            if (!isMobile) {
                setOpened(false);
            }
            deregisterScrollAway();
            deregisterClickAway();
            setOpened(false);
        }

        setFocused?.(false);
        setSearchValue('');
        onBlur?.();
    }, [opened, onBlur, isMobile, deregisterScrollAway, deregisterClickAway]);

    const open = useCallback(() => {
        if (
            disabled ||
            // We want to be able to open the dropdown if the select is in readonly mode and multiselect and compact are true and there is at least one selected item
            (readOnly && (!multiselect || !compact || (multiselect && Array.isArray(selected) && !selected.length)))
        ) {
            return;
        }

        inputRef.current?.focus();

        if (isMobile) {
            window.setTimeout(() => {
                drawerInputRef.current?.focus();
            }, 500);
        }

        if (opened) return;

        setOpened(true);
    }, [disabled, readOnly, multiselect, compact, selected, isMobile, opened]);

    const toggleDropdown = useCallback(() => (opened ? close() : open()), [opened, close, open]);

    const handleItemClick = useCallback(
        (e: BaseSyntheticEvent, val: InputValue) => {
            if (disabled) return;

            if (onChange) {
                if (multiselect) {
                    const prevSelected = selected as InputValue[];
                    const newSelected = prevSelected.includes(val)
                        ? prevSelected.filter((v) => v !== val)
                        : [...prevSelected, val];
                    onChange(e as ChangeEvent, newSelected);

                    // Only clear search if preserveMultiSelectSearch is false
                    if (!preserveMultiSelectSearch) {
                        setSearchValue('');
                    }
                } else {
                    onChange(e as ChangeEvent, val);
                    setSearchValue('');
                    close();
                }
            }

            if (multiselect) {
                if (inputRef.current && !isMobile && !compact) {
                    inputRef.current.focus();
                }
            } else {
                comboInputRef.current?.focus();
            }
        },
        [onChange, multiselect, selected, preserveMultiSelectSearch, close, isMobile, compact, disabled]
    );

    const handleSelectAll = useCallback(
        (allSelected: boolean) => {
            if (!preserveMultiSelectSearch) {
                setSearchValue('');
            }
            onSelectAll?.(allSelected);
        },
        [onSelectAll, preserveMultiSelectSearch]
    );

    // Memoized, filtered and mapped items
    const filteredAndMappedItems = useFilteredItems({
        children,
        searchValue,
        onSearchChange,
        customSearch,
        searchValueMapper,
        valueMapper,
        readOnly,
        multiselect,
        selected,
        selectAll,
        selectedIndex,
        indexChangeReason,
        keyboardHandled,
        handleItemClick,
        name,
        setSelectedIndex,
        setIndexChangeReason,
    });

    const { handleSelectAll: handleSelectAllAction } = useSelectAll({
        items: filteredAndMappedItems,
        onChange,
        onSelectAll: handleSelectAll,
    });
    const childrenCount = filteredAndMappedItems.length;

    // More Handlers
    const handleSearchValueChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value: val } = e.target;

            if (disableInputSearch || (inputValuePattern && !inputValuePattern.test(val))) return;

            setSearchValue(val);

            // Find the first non-group item index in filtered items
            const firstSelectableIndex = filteredAndMappedItems.findIndex((item) => !isOptionGroup(item));
            // If selectAll is enabled, offset by 1
            const initialIndex =
                selectAll && firstSelectableIndex !== -1 ? firstSelectableIndex + 1 : firstSelectableIndex;

            setSelectedIndex(initialIndex >= 0 ? initialIndex : 0);
            setIndexChangeReason('internal');

            onSearchChange?.(val);

            if (!opened && val.length > 0) {
                open();
            }
        },
        [disableInputSearch, inputValuePattern, opened, onSearchChange, open, selectAll, filteredAndMappedItems]
    );

    const handleListFocus = useCallback(() => {
        if (listComponentRef.current && opened) {
            listComponentRef.current.focus();
        }
    }, [opened]);

    const { handleKeyDown } = useKeyboardNavigation({
        // Base props
        readOnly,
        multiselect,
        disabled,
        selected,
        onChange,
        // State
        opened,
        selectedIndex,
        searchValue,
        // Computed
        filteredAndMappedItems,
        selectAll,
        // Handlers
        open,
        close,
        setSelectedIndex,
        setIndexChangeReason,
        setKeyboardHandled,
        handleSelectAllAction,
        handleItemClick,
        handleListFocus,
        footer,
        inputRef,
    });

    // Effects
    useEffect(() => {
        if (opened) {
            const roots = ((): ElementGetter[] => [
                () => comboInputRef.current,
                () => overlayRef.current,
                () => drawerRef.current,
            ])();

            registerClickAway({
                handler: close,
                roots,
            });

            if (!isMobile) {
                registerScrollAway({
                    handler: close,
                    roots,
                });
            }
        }
    }, [breakpointRange, close, isMobile, opened, registerClickAway, registerScrollAway]);

    useLayoutEffect(() => {
        if (!multiselect || !compact) return;

        const calculateHiddenTags = () => {
            const container = valueContainerRef.current;
            if (!container) return;

            container.scrollTop = 0;
            const containerBottom = container.getBoundingClientRect().bottom;

            const hiddenCount = Object.values(tagsRef.current).filter(
                (tag) => tag && tag.getBoundingClientRect().top >= containerBottom
            ).length;

            setRestTagCount(hiddenCount);
        };

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(calculateHiddenTags);
        });

        const container = valueContainerRef.current;

        if (container) {
            resizeObserver.observe(container);
            calculateHiddenTags();
        }

        return () => resizeObserver.disconnect();
    }, [multiselect, compact, selected]);

    useEffect(() => {
        if (disabled && opened) {
            close();
        }
    }, [disabled, opened, close]);

    // Render helpers
    const getDisplayValues = useCallback(() => {
        const displayedValues: Array<{ key: InputValue; val: ReactNode; disabled?: boolean }> = [];

        // Flatten the children to handle nested structure
        const flattenedChildren = flattenChildren(children);

        flattenedChildren.forEach((c) => {
            // Skip group headers
            if (isOptionGroup(c)) return;

            if (!isValidElement<Valued & LabeledDropdownItem & Disabled>(c)) return;

            const val = valueMapper(c);
            if (multiselect) {
                const index = (selected as Selected[]).indexOf(val);
                if (index !== -1) {
                    displayedValues[index] = {
                        key: val,
                        val: displayMapper(c),
                        disabled: c.props.disabled,
                    };
                }
            } else if (selected === val) {
                displayedValues.push({
                    key: val,
                    val: displayMapper(c),
                    disabled: c.props.disabled,
                });
            }
        });

        return displayedValues;
    }, [children, multiselect, selected, valueMapper, displayMapper]);

    const getDropdownMinWidth = useCallback(() => {
        if (sizeToAnchor) return;
        if (dropdownMinWidth) return dropdownMinWidth;

        // If the dropdown is virtualized and not sized, we need a min width to prevent the dropdown from collapsing
        return virtualized && !sizeToAnchor ? DEFAULT_DROPDOWN_MIN_WIDTH : undefined;
    }, [dropdownMinWidth, virtualized, sizeToAnchor]);

    const memoizedRenderDropdownIcon = useCallback(
        (props: Omit<RenderDropdownIconProps, 'toggle'>) =>
            renderDropdownIcon({
                toggle: toggleDropdown,
                open: props.open,
                disabled: props.disabled,
                readOnly: props.readOnly,
                multiselect,
                compact,
            }),
        [renderDropdownIcon, toggleDropdown, multiselect, compact]
    );

    const displayValues = useMemo(() => getDisplayValues(), [getDisplayValues]);
    const hasTags = multiselect && displayValues.length > 0;
    const displayedInputValue = get(['0', 'val'], displayValues);

    // Select All component
    const getSelectAllItem = useMemo(() => {
        if (!selectAll || !childrenCount || readOnly) return null;

        return (
            <SelectAllItem
                items={filteredAndMappedItems}
                onChange={onChange}
                onSelectAll={handleSelectAll}
                ariaSelected={selectedIndex === 0}
                focused={selectedIndex === 0}
                onMouseEnter={() => {
                    setSelectedIndex(0);
                    setIndexChangeReason('user');
                }}
                shouldScrollIntoView={indexChangeReason === 'user' && keyboardHandled}
            />
        );
    }, [
        selectAll,
        childrenCount,
        readOnly,
        filteredAndMappedItems,
        onChange,
        handleSelectAll,
        selectedIndex,
        indexChangeReason,
        keyboardHandled,
    ]);

    const mainInputProps = {
        'aria-haspopup': 'listbox',
        'aria-required': required,
        'aria-invalid': !!error,
        ...(name && error ? { 'aria-errormessage': `${name}-error` } : {}),
    };

    // Common props
    const inputProps = {
        disabled,
        readOnly,
        placeholder: typeof selected !== 'number' && isEmpty(selected) ? placeholder : '',
        value: searchValue,
        onFocus: handleFocus,
        onBlur: handleBlur,
        ref: inputRef,
        onChange: handleSearchValueChange,
        onKeyDown: handleKeyDown,
        size,
        id: name,
        role: 'combobox',
        type: inputType,
        autoComplete: autoCompleteAttribute,
        label,
        helper,
        captionIconHandler,
        captionIconWithTooltip,
        inline,
        insideCompoundField,
        keyboardEntryDisabled: disableInputSearch,
        inputMode: disableInputSearch ? 'none' : undefined,
        autoFocus,
        'data-test-display-value': displayedInputValue,
        'data-test-id': dataTestId,
        'aria-activedescendant':
            opened && filteredAndMappedItems[selectedIndex]?.props.value
                ? `${name}-${filteredAndMappedItems[selectedIndex].props.value}`
                : undefined,
        'aria-expanded': opened,
        'aria-controls': opened ? makeListId(name) : undefined,
    };

    // Footer height is 56px + 1px for the divider
    const listMaxHeight = footer && virtualized ? overlayMaxHeight - 57 : overlayMaxHeight;

    const listProps = {
        virtualized,
        defaultItemHeight,
        selectedIndex,
        maxHeight: listMaxHeight,
        emptyMessage,
        childrenCount,
        ref: listComponentRef,
        keyboardHandled,
        searchContainerHeight: searchContainerRef.current?.clientHeight || 0,
        selectAll,
        readOnly,
        'aria-labelledby': name,
        'aria-multiselectable': multiselect,
        id: makeListId(name),
        tabIndex: 0,
        onKeyDown: handleKeyDown,
    };

    const renderClearableHandler = useMemo(() => {
        if (!clearable || disabled || readOnly) return false;

        if (multiselect) {
            return !!(Array.isArray(selected) && selected.length);
        }

        return !!selected;
    }, [clearable, disabled, multiselect, readOnly, selected]);

    return (
        <>
            <ComboInputElement
                disabled={disabled || (readOnly && (!multiselect || !compact))}
                ref={comboInputRef}
                onClick={
                    !disabled &&
                    // We want to be able to open the dropdown if the select is in readonly mode and multiselect and compact are true and there is at least one selected item
                    (!readOnly || (multiselect && compact && Array.isArray(selected) && selected.length > 0))
                        ? open
                        : undefined
                }
                onKeyDown={handleKeyDown}
                tabIndex={-1}
                size={size}
                hasTags={hasTags}
                value={value}
                readOnly={readOnly}
                {...other}
            >
                {iconName || leadingImage ? (
                    <LeftDetailContainer>
                        {iconName && <Icon value={iconName} />}
                        {leadingImage && <Image src={leadingImage} />}
                    </LeftDetailContainer>
                ) : null}
                <ValueContainerElement
                    hasRightDetail={!!renderDropdownIcon}
                    size={size}
                    hasTags={hasTags}
                    compact={compact}
                    ref={valueContainerRef}
                >
                    {multiselect &&
                        displayValues.map(({ key, val, disabled: itemDisabled }, index) => (
                            <Chip
                                key={key}
                                ref={(el) => (tagsRef.current[key] = el)}
                                value={index}
                                disabled={disabled || itemDisabled || readOnly}
                                onRemove={(e) => {
                                    const newSelected = [...(selected as InputValue[])];
                                    newSelected.splice(index, 1);
                                    onChange?.(e as ChangeEvent, newSelected);
                                }}
                                data-test-chip={key}
                                readOnly={readOnly}
                                accessibleText={ensureString({ value: t(OptimusTextKey.Remove) })}
                            >
                                {val}
                            </Chip>
                        ))}
                    {!multiselect && !searchValue && (
                        <SingleValueElement as="div" size={size} disabled={disabled} ellipsis textStyle="bodyMedium">
                            {displayedInputValue}
                        </SingleValueElement>
                    )}
                    {(!multiselect || !compact || (multiselect && compact && displayValues.length === 0)) && (
                        <AutosizeInputComponent {...mainInputProps} {...inputProps} />
                    )}
                </ValueContainerElement>

                {multiselect && compact && restTagCount > 0 && (
                    <RestTagCounterContainer>+{restTagCount}</RestTagCounterContainer>
                )}

                <RightDetailContainer>
                    {suffix || trailingImage || loader ? (
                        <RightVariantContainer>
                            <Stack spacing="100" itemAlignment="center">
                                {suffix && <Affix>{suffix}</Affix>}
                                {trailingImage && <Image src={trailingImage} />}
                            </Stack>
                            {loader && (
                                <SpinnerContainer>
                                    <Spinner size="small" />
                                </SpinnerContainer>
                            )}
                        </RightVariantContainer>
                    ) : null}
                    <Stack spacing="50" itemAlignment="center">
                        {renderClearableHandler ? (
                            <BareIconButton
                                icon={IconName.CrossClose}
                                onClick={handleClearClick}
                                size="small"
                                data-test-button-clear
                                disabled={disabled}
                                aria-label={t(OptimusTextKey.ClearSelection) as string}
                            />
                        ) : null}
                        {memoizedRenderDropdownIcon({
                            open: opened,
                            disabled,
                            readOnly,
                        })}
                    </Stack>
                </RightDetailContainer>
            </ComboInputElement>
            {isMobile ? (
                <Drawer open={opened} closeHandler="close" onClose={close} lockScroll ref={drawerRef}>
                    <Stack
                        vertical
                        spacing={mobileOptionsHeader != null ? '200' : '100'}
                        distribution="space-between"
                        fill
                    >
                        <StackItem>
                            {!readOnly &&
                                (mobileOptionsHeader ?? (
                                    /* @ts-expect-error */
                                    <DrawerInput {...inputProps} size={size} ref={drawerInputRef} aria-label={name} />
                                ))}
                            <SelectList
                                {...listProps}
                                maxHeight="fit-content"
                                defaultItemHeight={mobileDefaultItemHeight}
                            >
                                {!readOnly && selectAll ? getSelectAllItem : null}
                                {filteredAndMappedItems}
                            </SelectList>
                        </StackItem>
                        {footer ? (
                            <DropdownFooter {...footer} close={close} isMobile listRef={listComponentRef} />
                        ) : null}
                    </Stack>
                </Drawer>
            ) : (
                <OpenableOverlay
                    minWidth={getDropdownMinWidth()}
                    sizeToAnchor={sizeToAnchor}
                    scrollOnOverflow
                    opened={opened}
                    anchorElement={() => comboInputRef.current}
                    innerRef={overlayRef}
                    ref={overlayComponentRef as unknown as Ref<Positioner>}
                    maxHeight={overlayMaxHeight}
                    portalContainerId={portalContainerId}
                    onMaxSizeChange={({ maxHeight }: MaxSizeChangeProps) =>
                        maxHeight && listComponentRef?.current?.updateMaxHeight(maxHeight)
                    }
                    onMount={() => {
                        if (inputRef.current && multiselect && compact) {
                            inputRef.current.focus();
                        }
                    }}
                    data-test-overlay-id={name}
                    footerProps={{
                        close,
                        listRef: listComponentRef,
                        virtualized,
                        primary: footer?.primary,
                        secondary: footer?.secondary,
                    }}
                >
                    <>
                        {!readOnly && multiselect && compact && (
                            <Container ref={searchContainerRef}>
                                <SearchContainerElement>
                                    {/* @ts-expect-error  */}
                                    <Input
                                        {...inputProps}
                                        indicativeIcon={IconName.Search}
                                        placeholder={searchPlaceholder}
                                        aria-label={name}
                                    />
                                </SearchContainerElement>
                                <Divider />
                            </Container>
                        )}
                        <SelectList {...listProps}>
                            {!readOnly && selectAll && getSelectAllItem}
                            {filteredAndMappedItems}
                        </SelectList>
                    </>
                </OpenableOverlay>
            )}
            {!multiselect && shadowField && <input type="hidden" name={name} value={selected?.toString() ?? ''} />}
        </>
    );
};

const SelectComponent = withBreakpointRange(withScrollAway(Select));
export default SelectComponent;
